/**
 * Vulnerability Actions Component
 *
 * Action bar for bulk vulnerability operations including:
 * - Annotate (Note, Tag, Remove Tag)
 * - Scan (Rescan Affected Assets, Confirm Affiliation)
 * - Export (Always-enabled export button)
 * - Status/Severity changes (via standard action pattern)
 *
 * Layout uses justify-between to separate selection-dependent actions (left)
 * from always-enabled export (right).
 *
 * @module sections/vulnerabilities/components/actions
 */

import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { isAxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { AskMarcusButton } from '@/components/actions/AskMarcusButton';
import { VulnerabilityManagedDropdown } from '@/components/actions/VulnerabilityManagedDropdown';
import { Dropdown } from '@/components/Dropdown';
import { DropdownOption } from '@/components/legacy-ui/Dropdown';
import {
  buildAnnotateAction,
  buildExportAction,
  buildGenericCustomAction,
  buildImportAction,
  buildScanActionHandlers,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import { Button } from '@/components/ui/button';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMy } from '@/hooks/useMy';
import { ExportFormat, type ExportFormatType } from '@/hooks/useReportExport';
import { useSummonAurelius } from '@/hooks/useSummonAurelius';
import { ExportPDFReport } from '@/sections/export/ExportPDFReport';
import { HackerOneSubmitModal } from '@/sections/vulnerabilities/components/modals/HackerOneSubmitModal';
import {
  getExploitButtonConfig,
  getExploitState,
  isConstantineFinding,
  useExploitVerification,
  useVulnerabilityActionHandlers,
} from '@/sections/vulnerabilities/hooks/actions';
import { useAuth } from '@/state/auth';
import { Risk } from '@/types';
import { cn } from '@/utils/classname';
import { canRescanAnyRisk } from '@/utils/risk.util';

/**
 * Props for VulnerabilityActions component
 */
export interface VulnerabilityActionsProps {
  /** Vulnerabilities to perform actions on */
  vulnerabilities: Risk[];
  /** Additional CSS classes */
  className?: string;
  /** Hide the "Actions:" label */
  hideLabel?: boolean;
  /** Handler for opening tag modal */
  onTagClick?: () => void;
  /** Handler for opening note modal */
  onNoteClick?: () => void;
  /** Handler for opening remove tag modal */
  onRemoveTagClick?: () => void;
  /** Handler for opening import modal */
  onImportClick: () => void;
  /** Handler for opening export modal */
  onExportClick: () => void;
  /** Selected risk keys to pass to PDF export (optional) */
  pdfSelectedRows?: string[];
  /** Handler for opening auxiliary info modal */
  onAuxInfoClick?: () => void;
  /** Handler for refreshing visited timestamp (Praetorian users only) */
  onRefreshVisited?: (comment: string) => void;
  /** Whether the refresh visited mutation is in progress */
  isRefreshingVisited?: boolean;
  /** Additional actions for "Ticketing" dropdown (Jira ticket actions) */
  moreActions?: DropdownOption[];
  /** PlexTrac-specific actions dropdown (Praetorian users only) */
  plexTracActions?: DropdownOption[];
  /** Handler for risk status update */
  onUpdateRisk?: (risk: Risk) => Promise<void>;
  /** Handler for clearing selection after actions */
  onClearSelection?: () => void;
  /**
   * Current bulk-selection state. When provided and shouldUseBulk() returns
   * true, the Manage dropdown intercepts at this boundary and calls
   * onBulkStatusChange instead of per-item onUpdateRisk.
   */
  selectionState?: BulkSelectionState;
  /**
   * Called ONCE when bulk mode is active and the user picks a new status (or reject).
   * Receives the FULL combined status string — e.g. 'OH', 'T', 'DHFalsePositive'.
   * First char is the lifecycle status (used for getBulkActionName labeling).
   * Caller (index.tsx) opens statusConfirm.openConfirmation() with params.status = combinedStatus.
   */
  onBulkStatusChange?: (combinedStatus: string) => void;
  /**
   * Called ONCE when bulk mode is active and the user picks a new severity.
   * Receives the severity char. Caller opens severityConfirm.openConfirmation().
   */
  onBulkSeverityChange?: (severityChar: string) => void;
  /** Handler called after scan actions complete */
  onScanComplete?: () => void;
  /** Severity filter options for dropdown */
  severityOptions?: Array<{
    label: string;
    value: string;
    id: string;
    severity?: string;
  }>;
  /** Function to get risk status label */
  getRiskStatusLabel?: (status: string) => { status: string; severity: string };
  /** Hide all action buttons */
  hideActions?: boolean;
  /** Disable actions when no items selected */
  disabled?: boolean;
  /**
   * When false, the Change Severity submenu is hidden.
   * Backend risk/service.go:64 rejects T* writes for non-Praetorian users on
   * accounts without vulnerability-status-system=new. Bulk severity_change
   * always writes T*, so every item would fail with "Invalid status".
   *
   * Derived in parent as: isPraetorianUser || (mode === 'new')
   * Default: true (preserves behavior for callers that don't know to gate).
   */
  canChangeSeverity?: boolean;
}

/**
 * VulnerabilityActions Component
 *
 * Renders action dropdown buttons for selected vulnerabilities.
 * Provides consistent action interface across the vulnerabilities section.
 *
 * @param props - Component props
 * @returns Action bar JSX
 *
 * @example
 * ```tsx
 * <VulnerabilityActions
 *   vulnerabilities={selectedRisks}
 *   onNoteClick={() => setShowNoteModal(true)}
 *   onTagClick={() => setShowTagModal(true)}
 *   onRemoveTagClick={() => setShowRemoveTagModal(true)}
 *   onExportClick={() => setShowExportModal(true)}
 *   onUpdateRisk={handleUpdateRisk}
 *   onClearSelection={clearSelection}
 *   severityOptions={severityFilters}
 *   getRiskStatusLabel={getRiskStatusLabel}
 * />
 * ```
 */
export function VulnerabilityActions({
  vulnerabilities,
  className,
  hideLabel,
  onNoteClick,
  onTagClick,
  onRemoveTagClick,
  onImportClick,
  onExportClick,
  pdfSelectedRows = [],
  onAuxInfoClick,
  onRefreshVisited,
  isRefreshingVisited = false,
  moreActions,
  plexTracActions,
  onUpdateRisk,
  onClearSelection,
  onScanComplete,
  selectionState,
  onBulkStatusChange,
  onBulkSeverityChange,
  severityOptions = [],
  getRiskStatusLabel,
  hideActions,
  disabled = false,
  canChangeSeverity = true,
}: VulnerabilityActionsProps) {
  const handlers = useVulnerabilityActionHandlers(
    vulnerabilities,
    onScanComplete
  );
  const { isPraetorianUser } = useAuth();
  const { summonAurelius } = useSummonAurelius();
  const { canWriteAssets, canWriteJobs } = useEntitlements();
  const { data: accounts = [] } = useMy({ resource: 'account' });
  const hackerOneAccount = accounts.find(
    (a: { member: string }) => a.member === 'hackerone'
  );
  const hackerOneProgramUrl = hackerOneAccount?.value
    ? `https://hackerone.com/${hackerOneAccount.value}`
    : '';
  const {
    handleExploit,
    isRunning: isExploitRunning,
    stopPolling,
  } = useExploitVerification(onScanComplete);

  const allConstantine =
    vulnerabilities.length > 0 && vulnerabilities.every(isConstantineFinding);
  const riskExploitState = allConstantine
    ? getExploitState(vulnerabilities)
    : 'ready';
  const exploitState = isExploitRunning ? 'running' : riskExploitState;

  // Stop polling when exploit results arrive on the risks
  useEffect(() => {
    if (
      isExploitRunning &&
      (riskExploitState === 'exploitable' ||
        riskExploitState === 'not_exploitable' ||
        riskExploitState === 'inconclusive')
    ) {
      stopPolling();
    }
  }, [isExploitRunning, riskExploitState, stopPolling]);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportFormat, setReportFormat] = useState<ExportFormatType>(
    ExportFormat.PDF
  );
  const [isHackerOneModalOpen, setIsHackerOneModalOpen] = useState(false);

  // Disable affiliation for non-Praetorian users
  const disableAffiliation = !isPraetorianUser;

  // Severity change handler with proper typing
  const handleSeverityChange = useMemo(
    () =>
      (option: {
        value?: string | number;
        label: string;
        severity?: string;
      }) => {
        if (!option?.value) return;

        // Bulk intercept: route through bulk confirmation once.
        if (
          selectionState &&
          shouldUseBulk(selectionState) &&
          onBulkSeverityChange
        ) {
          onBulkSeverityChange(String(option.value));
          return;
        }

        if (!onUpdateRisk || !getRiskStatusLabel) return;

        // Single-row path: update each item then clear selection once.
        const promises = vulnerabilities.map(risk => {
          const currentStatus = getRiskStatusLabel(risk.status).status;
          const newSeverity = String(option.value);
          const substatus = risk.status.slice(2);
          const combinedStatus = `${currentStatus}${newSeverity}${substatus}`;
          return onUpdateRisk({
            ...risk,
            status: combinedStatus,
          });
        });
        Promise.allSettled(promises).then(results => {
          const rejected = results.filter(r => r.status === 'rejected');
          if (rejected.length > 0) {
            console.error(
              `Severity change: ${rejected.length} item(s) failed`,
              rejected
            );
            const hasTimeout = rejected.some(r => {
              const reason = (r as PromiseRejectedResult).reason;
              return (
                isAxiosError(reason) &&
                (reason.response?.status === 504 ||
                  reason.response?.status === 408)
              );
            });
            toast.error('Failed to change severity', {
              description: hasTimeout
                ? 'The request may still be processing. Check the status before retrying.'
                : 'Please try again. If the problem persists, contact support@praetorian.com.',
              duration: 8000,
            });
          }
          if (rejected.length < results.length) {
            onClearSelection?.();
          }
        });
      },
    [
      vulnerabilities,
      onUpdateRisk,
      getRiskStatusLabel,
      onClearSelection,
      selectionState,
      onBulkSeverityChange,
    ]
  );

  // Build action configurations
  const actions = [
    buildGenericCustomAction({
      render: () => (
        <AskMarcusButton
          onClick={() =>
            summonAurelius({
              type: 'vulnerability',
              keys: vulnerabilities.map(v => v.key),
              displayNames: vulnerabilities.map(v => v.name),
            })
          }
          disabled={disabled}
          data-testid="vulnerability-ask-marcus-button"
        />
      ),
      position: 'first',
      testId: 'vulnerability-ask-marcus-wrapper',
    }),

    ...(canWriteAssets
      ? [
          buildAnnotateAction({
            onCommentClick: onNoteClick || (() => {}),
            onTagClick: onTagClick || (() => {}),
            onRemoveTagClick: onRemoveTagClick || (() => {}),
            labels: { comment: 'Add Note' },
            testId: 'vulnerability-annotate-dropdown',
          }),
        ]
      : []),

    // Manage dropdown combining Status, Severity, and Aux Info (after Annotate)
    // Uses type: 'Manage' for proper action ordering (after Annotate, before Scan)
    ...(canWriteAssets && (onUpdateRisk || onAuxInfoClick)
      ? [
          {
            type: 'Manage' as const,
            // Stub handlers satisfy ManageActionConfig type; render() overrides rendering
            handlers: {
              onFreezeClick: () => {},
              onActivateClick: () => {},
              onDeleteClick: () => {},
            },
            render: () => (
              <VulnerabilityManagedDropdown
                risks={vulnerabilities}
                onUpdateRisk={
                  onUpdateRisk
                    ? async (risk, _comment) => {
                        // Single-row path only. Bulk is intercepted inside the dropdown
                        // before this callback is ever reached.
                        await onUpdateRisk(risk);
                        onClearSelection?.(); // Major 8 fix: restore selection clear
                      }
                    : undefined
                }
                selectionState={selectionState}
                onBulkStatusRequested={combinedStatus => {
                  // combinedStatus is e.g. 'OH', 'TH', 'A', 'DHFalsePositive'.
                  // Backend writes params.status verbatim.
                  // getBulkActionName receives the FIRST char only for labeling.
                  onBulkStatusChange?.(combinedStatus);
                }}
                onBulkRejectRequested={combinedStatus => {
                  // combinedStatus is 'D{severity}{substatus}', e.g. 'DHFalsePositive'.
                  // Same routing as status change — bulk endpoint action:'status_change'
                  // regardless of whether the lifecycle status is delete or any other.
                  onBulkStatusChange?.(combinedStatus);
                }}
                getRiskStatusLabel={getRiskStatusLabel}
                severityOptions={severityOptions}
                onSeverityChange={
                  canChangeSeverity ? handleSeverityChange : undefined
                }
                onAuxInfoClick={onAuxInfoClick}
                onRefreshVisited={onRefreshVisited}
                isRefreshingVisited={isRefreshingVisited}
                disabled={disabled}
                data-testid="vulnerability-manage-dropdown"
                label="Manage"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
              />
            ),
            testId: 'vulnerability-manage-wrapper',
          },
        ]
      : []),

    ...(canWriteJobs
      ? [
          buildScanActionHandlers({
            handlers: {
              onRescanClick: () => handlers.handleRescan(vulnerabilities),
              onAffiliationClick: () =>
                handlers.handleConfirmAffiliation(vulnerabilities),
            },
            // Rescan re-runs the originating capability; manual/agent-filed
            // risks have no capability to re-run, so hide the action when
            // every selected risk is manual.
            hideActions: {
              rescan:
                vulnerabilities.length > 0 &&
                !canRescanAnyRisk(vulnerabilities),
            },
            disabledActions: { affiliation: disableAffiliation },
            testId: 'vulnerability-scan-dropdown',
          }),
        ]
      : []),

    // Exploit verification — only for Constantine findings
    ...(canWriteJobs && allConstantine
      ? [
          buildGenericCustomAction({
            render: () => {
              const buttonConfig = getExploitButtonConfig(exploitState);
              const isCompleted =
                exploitState === 'exploitable' ||
                exploitState === 'not_exploitable' ||
                exploitState === 'inconclusive';
              return (
                <Button
                  onClick={() => handleExploit(vulnerabilities)}
                  disabled={
                    disabled || exploitState === 'running' || isCompleted
                  }
                  size="sm"
                  className={cn(
                    'rounded-full',
                    buttonConfig.className,
                    isCompleted && 'cursor-default'
                  )}
                  data-testid="vulnerability-exploit-button"
                >
                  {buttonConfig.icon}
                  {buttonConfig.label}
                </Button>
              );
            },
            testId: 'vulnerability-exploit-wrapper',
          }),
        ]
      : []),

    ...(isPraetorianUser
      ? [
          buildGenericCustomAction({
            render: () => (
              <Dropdown
                placeholder="HackerOne"
                options={[
                  {
                    label: 'Submit to HackerOne',
                    value: 'hackerone',
                    onClick: () => setIsHackerOneModalOpen(true),
                  },
                ]}
                styleType="rounded"
                size="sm"
                className="[&_span:nth-child(3)]:hidden"
                listClassName="right-0"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
                data-testid="vulnerability-hackerone-dropdown"
              />
            ),
            testId: 'vulnerability-hackerone-wrapper',
          }),
        ]
      : []),

    buildShareAction({
      variant: 'button-only',
      onShareClick: handlers.handleShare,
      testId: 'vulnerability-share-button',
    }),

    ...(canWriteAssets
      ? [
          buildImportAction({
            onImportClick,
            disabled: false, // Always enabled
            testId: 'vulnerability-import-button',
          }),
        ]
      : []),

    buildExportAction({
      onExportClick,
      disabled: disabled,
      alwaysEnabled: false,
      testId: 'vulnerability-export-button',
    }),

    ...(isPraetorianUser
      ? [
          buildGenericCustomAction({
            render: () => {
              const reportOptions = [
                {
                  label: 'Generate PDF',
                  value: 'generate-pdf',
                  onClick: () => {
                    setReportFormat('pdf');
                    setReportModalOpen(true);
                  },
                },
                {
                  label: 'Generate ZIP',
                  value: 'generate-zip',
                  onClick: () => {
                    setReportFormat('zip');
                    setReportModalOpen(true);
                  },
                },
              ];

              return (
                <>
                  <Dropdown
                    placeholder="Generate Report"
                    options={reportOptions}
                    styleType="rounded"
                    size="sm"
                    className="border-dashed [&_span:nth-child(3)]:hidden"
                    listClassName="right-0"
                    startIcon={<DocumentTextIcon className="size-4" />}
                    data-testid="vulnerability-report-dropdown"
                  />
                  <ExportPDFReport
                    selectedRows={pdfSelectedRows}
                    open={reportModalOpen}
                    onOpenChange={setReportModalOpen}
                    initialExportFormat={reportFormat}
                  />
                </>
              );
            },
            testId: 'vulnerability-report-wrapper',
          }),
        ]
      : []),

    ...(canWriteAssets && plexTracActions && plexTracActions.length > 0
      ? [
          buildGenericCustomAction({
            render: () => (
              <Dropdown
                placeholder="PlexTrac"
                options={plexTracActions}
                styleType="rounded"
                size="sm"
                className="[&_span:nth-child(3)]:hidden"
                listClassName="right-0"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
                data-testid="vulnerability-plextrac-dropdown"
              />
            ),
            testId: 'vulnerability-plextrac-wrapper',
          }),
        ]
      : []),

    ...(canWriteAssets && moreActions && moreActions.length > 0
      ? [
          buildGenericCustomAction({
            render: () => (
              <Dropdown
                placeholder="Ticketing"
                options={moreActions}
                styleType="rounded"
                size="sm"
                className="[&_span:nth-child(3)]:hidden"
                listClassName="right-0"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
                data-testid="vulnerability-ticketing-dropdown"
              />
            ),
            testId: 'vulnerability-ticketing-wrapper',
          }),
        ]
      : []),
  ];

  return (
    <>
      <TableActionsBar
        selectedItems={vulnerabilities}
        actions={actions}
        disabled={disabled}
        className={className}
        hideLabel={hideLabel}
        hideActions={hideActions}
        testIdPrefix="vulnerability"
      />
      {isPraetorianUser && (
        <HackerOneSubmitModal
          open={isHackerOneModalOpen}
          onClose={() => setIsHackerOneModalOpen(false)}
          vulnerabilities={vulnerabilities}
          programUrl={hackerOneProgramUrl}
          onSubmit={async params => {
            await handlers.handleSubmitToHackerOne(vulnerabilities, params);
            setIsHackerOneModalOpen(false);
          }}
        />
      )}
    </>
  );
}
